import { Injectable } from '@angular/core';
import { SdgCardModel } from './models';
import cardsJSON from '../../../assets/json/sdgcards.json';
import { AbstractService } from '../../shared/services/abstract.service';
import { ScoringModel, ScoringWrapperModel } from './models/scoring.model';
import { ScoreboardState } from './xs';
import { Assessment, Topic } from './models/topic.model';
import { BehaviorSubject } from 'rxjs';
import { EsgPopupAssessment, EsgPopupProgressItem } from './esg/popup/esg-popup.model';
import { SliderGroupState } from './shared/slider-toggle/xs/slider-group.state';

@Injectable({ providedIn: 'root' })
export class ScoreboardService extends AbstractService {
  private topicSource = new BehaviorSubject<Topic[] | undefined>(undefined);

  // eslint-disable-next-line no-invalid-this
  currentTopic = this.topicSource.asObservable();

  updateScoreSelection(scores: Topic[]): void {
    this.topicSource.next(scores);
  }

  getCurrentTopic(): Topic[] | undefined {
    return this.topicSource.getValue();
  }

  public isFirstResultsCheck(): boolean {
    return this.store.selectSnapshot(ScoreboardState.isFirstResultsCheck);
  }

  async getAllScores(): Promise<Topic[]> {
    const result = await this.http.get(`${this.apiBaseUrl}/assessments/esg/scores`).toPromise();

    return result as Topic[];
  }

  public async getScoringFromBackend(id: string): Promise<ScoringModel> {
    return (await this.http.get(`${this.apiBaseUrl}/scorings/${id}`).toPromise()) as ScoringModel;
  }

  public async getLatestScoringFromBackend(): Promise<ScoringWrapperModel> {
    return (await this.http.get(`${this.apiBaseUrl}/assessments/esg/scores/latest`).toPromise()) as ScoringWrapperModel;
  }

  public getScoring(): ScoringWrapperModel | undefined {
    return this.store.selectSnapshot(ScoreboardState.getScoringModel);
  }

  public getSelectedAssessment(): string | null | undefined {
    return this.store.selectSnapshot(ScoreboardState.getSelectedAssessment);
  }

  public async getManagedAssessmentScoresFromBackend(assessmentId: string): Promise<ScoringWrapperModel> {
    return (await this.http
      .get(`${this.apiBaseUrl}/assessments/esg/${assessmentId}/scores`)
      .toPromise()) as ScoringWrapperModel;
  }

  getCards(): SdgCardModel[] {
    return JSON.parse(JSON.stringify(cardsJSON.cards)) as SdgCardModel[];
  }

  downloadReport(scoringId: string): Promise<Blob | undefined> {
    const language = this.store.selectSnapshot(SliderGroupState.getLanguage);

    return this.http
      .post(`${this.apiBaseUrl}/scorings/${scoringId}/report?locale=${language}`, {}, { responseType: 'blob' })
      .toPromise();
  }

  downloadLegacyReport(scoringId: string): Promise<Blob | undefined> {
    const language = this.store.selectSnapshot(SliderGroupState.getLanguage);

    return this.http
      .get(`${this.apiBaseUrl}/scorings/${scoringId}/report-old?locale=${language}`, { responseType: 'blob' })
      .toPromise();
  }

  downloadEsgImage(scoringId: string): Promise<Blob | undefined> {
    const language = this.store.selectSnapshot(SliderGroupState.getLanguage);

    return this.http
      .get(`${this.apiBaseUrl}/scorings/${scoringId}/esg-img?locale=${language}`, { responseType: 'blob' })
      .toPromise();
  }

  downloadSdgImage(scoringId: string): Promise<Blob | undefined> {
    const language = this.store.selectSnapshot(SliderGroupState.getLanguage);

    return this.http
      .get(`${this.apiBaseUrl}/scorings/${scoringId}/sdg-img?locale=${language}`, { responseType: 'blob' })
      .toPromise();
  }

  downloadStatement(scoringId: string): Promise<Blob | undefined> {
    const language = this.store.selectSnapshot(SliderGroupState.getLanguage);

    return this.http
      .get(`${this.apiBaseUrl}/scorings/${scoringId}/commitment-img?locale=${language}`, { responseType: 'blob' })
      .toPromise();
  }

  buildEsgPopupAssessments(category: string, language: string): EsgPopupAssessment[] {
    const scores = this.getCurrentTopic();

    if (!scores) {
      return [];
    }
    const formattedLanguage = language.replace('_', '-').replace('GB', 'UK');
    const esgAssessments: EsgPopupAssessment[] = [];
    // to do: remove color from progress item

    scores.forEach((score) => {
      score.assessments.forEach((assessment) => {
        const progressItems: EsgPopupProgressItem[] = [];
        const old = assessment.scoring.deprecatedLatest;

        if (!assessment.selected) {
          return;
        }

        if (!old) {
          this.addProgressItems(assessment, category, formattedLanguage, progressItems);
        }

        const popupAssessment: EsgPopupAssessment = {
          id: assessment.id,
          name: assessment.name,
          items: progressItems,
          old,
        };

        esgAssessments.push(popupAssessment);
      });
    });

    return esgAssessments;
  }

  private addProgressItems(
    assessment: Assessment,
    category: string,
    formattedLanguage: string,
    progressItems: EsgPopupProgressItem[],
  ): void {
    assessment.scoring.scoring.esg.forEach((esg) => {
      if (esg.category.toLowerCase() !== category) {
        return;
      }

      esg.subcategoryTranslations.forEach((subcategoryTranslation) => {
        if (subcategoryTranslation.language !== formattedLanguage) {
          return;
        }

        subcategoryTranslation.subcategories.forEach((subcategory) => {
          const progressItem: EsgPopupProgressItem = {
            title: subcategory.subcategory,
            value: subcategory.score,
            maxValue: subcategory.maxScore,
            color: esg.category,
          };

          progressItems.push(progressItem);
        });
      });
    });
  }
}
